/** Pure catalog operations. The captured renderer remains a compiled dependency. */
export function normalizeModelName(value) {
  return String(value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Both renderer IDs and visible names are accepted. Availability is explicit:
 * a catalog entry that lacks `available: true` must never appear as installed.
 * @param {Array<Record<string, any>>} catalog
 */
export function createModelRegistry(catalog) {
  if (!Array.isArray(catalog)) throw new TypeError('Model catalog must be an array');
  const aliases = new Map();
  const ids = new Set();
  const models = catalog.map(record => {
    if (!record || typeof record.id !== 'string' || typeof record.label !== 'string') {
      throw new TypeError('Each model needs an id and label');
    }
    const key = normalizeModelName(record.id);
    if (!key || ids.has(key)) throw new Error(`Duplicate or empty model id: ${record.id}`);
    ids.add(key);
    return Object.freeze({...record});
  });
  for (const model of models) {
    for (const name of [model.id, model.label, ...(model.aliases || [])]) {
      const key = normalizeModelName(name);
      if (!key) throw new Error(`Empty alias for model: ${model.id}`);
      if (aliases.has(key) && aliases.get(key) !== model) {
        throw new Error(`Ambiguous model alias: ${name}`);
      }
      aliases.set(key, model);
    }
  }
  // Longest first: a selected "iPhone 18 Pro Max · ..." must not match Pro.
  const prefixes = [...aliases.keys()].sort((a, b) => b.length - a.length);
  const resolve = value => aliases.get(normalizeModelName(value));
  const available = Object.freeze(models.filter(model => model.available === true));
  return Object.freeze({
    models: Object.freeze(models),
    available,
    resolve,
    isAvailable: value => resolve(value)?.available === true,
    resolveSelection(value) {
      const key = normalizeModelName(value);
      return aliases.get(prefixes.find(prefix => key.startsWith(prefix)));
    },
    librarySummary() {
      const flat = available.find(model => model.kind === 'flat');
      const count = available.filter(model => model.kind !== 'flat').length;
      return `${count} ${count === 1 ? 'model' : 'models'}${flat ? ` + ${flat.label}` : ''} · Ready to use in this shot`;
    }
  });
}
